import { Component, OnInit, ViewChild, OnDestroy } from '@angular/core';

import { ActivatedRoute, Router } from '@angular/router';
import {} from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslateModule } from '@ngx-translate/core';
import { NgSelectModule } from '@ng-select/ng-select';
import { faSpinner, faExclamationTriangle, faArrowLeft, faCircleInfo, faDownload, faChevronLeft, faChevronRight, faShieldHalved, faCheck, faXmark, faRotateRight, faFlag } from '@fortawesome/free-solid-svg-icons';
import { CommonModule } from '@angular/common';

import { ViewerComponent } from '../../components/viewer/viewer.component';
import { EntitiesViewComponent } from '../../components/entities-view/entities-view.component';
import { ClassificationsViewComponent } from '../../components/classifications-view/classifications-view.component';
import { ResultsService } from '../../services/results.service';
import { PreviewCacheService, PreviewMetadata } from '../../services/preview-cache.service';
import { ModalExecutionDetailsComponent } from '../../components/modal-execution-details/modal-execution-details.component';
import { ModalSaveReferenceComponent } from '../../components/modal-save-reference/modal-save-reference.component';
import { ModalConfirmComponent } from '../../components/modal-confirm/modal-confirm.component';
import { ButtonComponent } from '../../components/button/button.component';
import { ReferencesService } from '../../services/references.service';
import { AlertService } from '../../components/alert-service/alert-service';
import { TranslateService } from '@ngx-translate/core';
import { TenantService } from '../../services/tenant.service';
import { TruncateMiddlePipe } from '../../pipes/truncate-middle.pipe';

@Component({
  selector: 'app-result',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    FontAwesomeModule,
    TranslateModule,
    ViewerComponent,
    EntitiesViewComponent,
    ClassificationsViewComponent,
    ModalExecutionDetailsComponent,
    ModalSaveReferenceComponent,
    ModalConfirmComponent,
    ButtonComponent,
    NgSelectModule,
    TruncateMiddlePipe
],
  templateUrl: './result.component.html',
  styleUrls: ['./result.component.scss']
})
export class ResultComponent implements OnInit, OnDestroy {
  @ViewChild(ViewerComponent) viewerComponent!: ViewerComponent;

  interactionId: string = '';
  resultId: string = '';
  interactionName: string = '';
  resultDate: string = '';
  resultNumber: number | null = null;
  resultName: string = '';
  
  archives: any[] = [];
  selectedArchiveId: string = '';
  selectedArchive: any = null;
  
  predictData: any = null;

  previewPages: string[] = [];
  previewMetadata: PreviewMetadata | null = null;
  previewBatchLoading = false;
  entities: any[] = [];
  objects: any[] = [];
  classifications: any[] = [];
  viewerImgIndex: number = 0;
  selectedEntityId: number | null = null;
  
  isBlocked = false;
  blockedReason: string = '';
  blockedType: string | null = null;
  blockedScore: number | null = null;
  blockedChunk: string | null = null;
  feedbackSent: 'safe' | 'unsafe' | null = null;
  sendingFeedback = false;
  reprocessing = false;
  reprocessError: string = '';
  reprocessProgress = 0;
  reprocessStage = '';
  private reprocessPoll?: any;

  private readonly REPROCESS_STAGE_LABELS: Record<string, string> = {
    ocr: 'RESULT.BLOCKED.STAGES.OCR',
    extracting: 'RESULT.BLOCKED.STAGES.EXTRACTING',
    saving: 'RESULT.BLOCKED.STAGES.SAVING',
    done: 'RESULT.BLOCKED.STAGES.DONE',
  };

  get reprocessStageLabel(): string {
    return this.REPROCESS_STAGE_LABELS[this.reprocessStage] || 'RESULT.BLOCKED.REPROCESSING';
  }

  loadingSnapshot = false;
  loadingOutputs = false;
  loadingPreview = false;

  snapshotError: string = '';
  outputsError: string = '';
  previewError: string = '';
  
  showExecutionModal = false;
  showSaveReferenceModal = false;
  savingReference = false;
  initialReferenceName = '';

  reportingUnsafe = false;
  reportedUnsafe = false;
  showReportUnsafeModal = false;
  reportUnsafeTarget: 'document' | 'prompt' = 'document';

  icons = {
    faSpinner,
    faExclamationTriangle,
    faArrowLeft,
    faCircleInfo,
    faDownload,
    faChevronLeft,
    faChevronRight,
    faShieldHalved,
    faCheck,
    faXmark,
    faRotateRight,
    faFlag
  };

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private resultsService: ResultsService,
    private previewCache: PreviewCacheService,
    private referencesService: ReferencesService,
    private alert: AlertService,
    private translate: TranslateService,
    private tenantService: TenantService
  ) {}

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.interactionId = params['interactionId'];
      this.resultId = params['resultId'];
      
      if (this.interactionId && this.resultId) {
        this.loadInteractionSnapshot();
      }
    });
  }

  loadInteractionSnapshot(): void {
    this.loadingSnapshot = true;
    this.snapshotError = '';
    
    this.resultsService.getInteractionSnapshot(this.interactionId, this.resultId)
      .subscribe({
        next: (response: any) => {
          this.loadingSnapshot = false;
          
          if (response.interaction_snapshot) {
            if (response.interaction_snapshot.interaction_name) {
              this.interactionName = response.interaction_snapshot.interaction_name;
            }
            if (response.interaction_snapshot.result_created_at) {
              this.resultDate = new Date(response.interaction_snapshot.result_created_at).toLocaleString();
            }
            if (response.interaction_snapshot.result_number) {
              this.resultNumber = response.interaction_snapshot.result_number;
              this.resultName = `Resultado ${this.resultNumber}`;
            }
            
            if (response.interaction_snapshot.archives) {
              this.archives = [...response.interaction_snapshot.archives].sort((a, b) => a.name.localeCompare(b.name));
            
            if (this.archives.length > 0) {
              this.selectedArchiveId = this.archives[0].id;
              this.onArchiveSelect();
            }
          }
          }
        },
        error: (error: any) => {
          this.loadingSnapshot = false;
          this.snapshotError = error.error?.message || 'ERROR.LOADING.SNAPSHOT';
        }
      });
  }

  onArchiveSelect(): void {
    if (!this.selectedArchiveId) {
      return;
    }
    
    this.entities = [];
    this.selectedEntityId = null;
    this.viewerImgIndex = 0;
    
    this.loadOutputs();
    this.loadPreview();
  }

  private _resetOutputState(): void {
    this.predictData = null;
    this.classifications = [];
    this.isBlocked = false;
    this.blockedReason = '';
    this.blockedType = null;
    this.blockedScore = null;
    this.blockedChunk = null;
    this.feedbackSent = null;
    this.reprocessing = false;
    this.reprocessError = '';
    this.reportingUnsafe = false;
    this.reportedUnsafe = false;
    this.entities = [];
    this.objects = [];
  }

  private _applyPredictData(data: any): void {
    this.predictData = data;
    if (data?.blocked === true) {
      this.isBlocked = true;
      this.blockedReason = data.reason || '';
      this.blockedType = data.blocked_type || null;
      this.blockedScore = data.score ?? null;
      this.blockedChunk = data.blocked_chunk || null;
      this.entities = [];
      this.objects = [];
      this.classifications = [];
    } else if (data?.classifications) {
      this.classifications = [...data.classifications].sort((a: any, b: any) => b.score - a.score);
      this.entities = [];
      this.objects = [];
    } else if (data?.result?.entities) {
      this.entities = data.result.entities;
      this.objects = data.result.objects || [];
    } else if (data?.entities) {
      this.entities = data.entities;
      this.objects = data.objects || [];
    }
  }

  loadOutputs(): void {
    this.loadingOutputs = true;
    this.outputsError = '';
    this._resetOutputState();

    this.resultsService.getResultOutputs(
      this.interactionId,
      this.resultId,
      this.selectedArchiveId
    ).subscribe({
      next: (response: any) => {
        this.loadingOutputs = false;
        if (response.outputs) {
          try {
            const raw = response.outputs.predict_data;
            this._applyPredictData(typeof raw === 'string' ? JSON.parse(raw) : raw);
          } catch (e) {
            this.predictData = response.outputs.predict_data;
          }
        }
      },
      error: (error: any) => {
        this.loadingOutputs = false;
        this.outputsError = error.error?.message || 'ERROR.LOADING.OUTPUTS';
      }
    });
  }

  loadPreview(): void {
    this.loadingPreview = true;
    this.previewError = '';
    this.previewPages = [];
    this.previewMetadata = null;
    
    this.loadPreviewBatch(1);
  }

  private loadPreviewBatch(page: number): void {
    if (page > 1) {
      this.previewBatchLoading = true;
    }

    this.previewCache.getPreviewPages(this.selectedArchiveId, page).subscribe({
      next: ({ pages, metadata }) => {
        if (page === 1) {
          this.previewPages = pages.map(p => p.base64Data);
        } else {
          this.previewPages = [...this.previewPages, ...pages.map(p => p.base64Data)];
        }
        this.previewMetadata = metadata;
        this.loadingPreview = false;
        this.previewBatchLoading = false;
      },
      error: (error: any) => {
        this.loadingPreview = false;
        this.previewBatchLoading = false;
        this.previewError = error.error?.message || 'ERROR.LOADING.PREVIEW';
      }
    });
  }

  onPreviewPageChange(newIndex: number): void {
    this.viewerImgIndex = newIndex;
    
    if (!this.previewMetadata) return;
    if (this.previewBatchLoading) return;

    const loadedPages = this.previewPages.length;
    const requestedPageNumber = newIndex + 1;

    if (requestedPageNumber > loadedPages) {
      const nextBatchPage = Math.ceil(requestedPageNumber / this.previewMetadata.pageSize);
      this.loadPreviewBatch(nextBatchPage);
    }
  }

  getArchiveName(archiveId: string): string {
    const archive = this.archives.find(a => a.id === archiveId);
    return archive ? archive.name : `Archive ${archiveId.substring(0, 8)}`;
  }

  onEntityClick(entityId: number): void {
    if (this.selectedEntityId === entityId) {
      this.selectedEntityId = null;
      if (this.viewerComponent) {
        this.viewerComponent.resetView();
      }
    } else {
      this.selectedEntityId = entityId;
      if (this.viewerComponent) {
        this.viewerComponent.focusOnEntity(this.selectedEntityId);
      }
    }
  }

  onClassificationClick(classification: any): void {
    if (classification && typeof classification.page === 'number') {
      const pageIndex = classification.page - 1;
      if (pageIndex >= 0) {
        this.onPreviewPageChange(pageIndex);
      }
    }
  }

  onViewerEntityClick(entityId: number): void {
    if (this.selectedEntityId === entityId) {
      this.selectedEntityId = null;
      if (this.viewerComponent) {
        this.viewerComponent.resetView();
      }
    } else {
      this.selectedEntityId = entityId;
    }
  }

  sendFeedback(userLabel: 'safe' | 'unsafe'): void {
    if (this.sendingFeedback || this.feedbackSent) return;
    this.sendingFeedback = true;
    this.resultsService.sendGuardFeedback(this.interactionId, this.resultId, this.selectedArchiveId, userLabel).subscribe({
      next: () => {
        this.feedbackSent = userLabel;
        this.sendingFeedback = false;
        this.alert.showSuccess(this.translate.instant('RESULT.BLOCKED.FEEDBACK_SENT'));
      },
      error: (err: any) => {
        this.sendingFeedback = false;
        this.alert.showError(err, this.translate.instant('RESULT.BLOCKED.FEEDBACK_ERROR'));
      }
    });
  }

  reprocessWithoutGuard(): void {
    if (this.reprocessing) return;
    this.reprocessing = true;
    this.reprocessError = '';
    this.reprocessProgress = 0;
    this.reprocessStage = 'starting';
    this._startReprocessPolling();
    this.resultsService.reprocessWithoutGuard(this.interactionId, this.resultId, this.selectedArchiveId).subscribe({
      next: (response: any) => {
        this._stopReprocessPolling();
        this.reprocessProgress = 100;
        this.reprocessStage = 'done';
        this.resultsService.clearCache();
        this._resetOutputState();
        this._applyPredictData(response.result);
        this.alert.showSuccess(this.translate.instant('RESULT.BLOCKED.REPROCESS_SUCCESS'));
      },
      error: (err: any) => {
        this._stopReprocessPolling();
        this.reprocessing = false;
        this.reprocessError = err.error?.detail || 'RESULT.BLOCKED.REPROCESS_ERROR';
        this.alert.showError(err, this.translate.instant('RESULT.BLOCKED.REPROCESS_ERROR'));
      }
    });
  }

  private _startReprocessPolling(): void {
    this._stopReprocessPolling();
    this.reprocessPoll = setInterval(() => {
      this.resultsService.listResults(this.interactionId).subscribe({
        next: (response: any) => {
          const results = response?.results || response?.all_results || [];
          const current = Array.isArray(results) ? results.find((r: any) => r.id === this.resultId) : null;
          const pct = current?.progress?.percentage;
          if (typeof pct === 'number') {
            this.reprocessProgress = pct;
          }
          const stage = current?.progress?.stage;
          if (stage) {
            this.reprocessStage = stage;
          }
        },
        error: () => {}
      });
    }, 1500);
  }

  private _stopReprocessPolling(): void {
    if (this.reprocessPoll) {
      clearInterval(this.reprocessPoll);
      this.reprocessPoll = undefined;
    }
  }

  openReportUnsafeModal(): void {
    if (this.reportingUnsafe || this.reportedUnsafe) return;
    this.reportUnsafeTarget = 'document';
    this.showReportUnsafeModal = true;
  }

  closeReportUnsafeModal(): void {
    this.showReportUnsafeModal = false;
  }

  confirmReportAsUnsafe(): void {
    this.reportingUnsafe = true;
    this.resultsService.reportAsUnsafe(this.interactionId, this.resultId, this.selectedArchiveId, this.reportUnsafeTarget).subscribe({
      next: () => {
        this.reportingUnsafe = false;
        this.reportedUnsafe = true;
        this.showReportUnsafeModal = false;
        this.alert.showSuccess(this.translate.instant('RESULT.REPORT_UNSAFE.SUCCESS'));
      },
      error: (err: any) => {
        this.reportingUnsafe = false;
        this.showReportUnsafeModal = false;
        this.alert.showError(err, this.translate.instant('RESULT.REPORT_UNSAFE.ERROR'));
      }
    });
  }

  goBack(): void {
    this.router.navigate(this.tenantService.buildRoute(['/simulations', this.interactionId]));
  }

  showExecutionDetails(): void {
    this.showExecutionModal = true;
  }

  closeExecutionDetails(): void {
    this.showExecutionModal = false;
  }

  openSaveReferenceModal(): void {
    if (!this.selectedArchiveId) {
      return;
    }

    const fileName = this.getArchiveName(this.selectedArchiveId).replace(/\.[^/.]+$/, '');
    const defaultName = fileName || this.resultName || this.interactionName;
    this.initialReferenceName = (defaultName || '').trim().replace(/\s+/g, '_');
    this.showSaveReferenceModal = true;
  }

  closeSaveReferenceModal(): void {
    if (this.savingReference) {
      return;
    }
    this.showSaveReferenceModal = false;
  }

  onReferenceSave(referenceName: string): void {
    if (!this.selectedArchiveId) {
      return;
    }

    this.savingReference = true;
    this.referencesService.createReference(this.resultId, {
      name: referenceName,
      interaction_id: this.interactionId,
      reference_id: this.selectedArchiveId
    }).subscribe({
      next: () => {
        this.alert.showSuccess(this.translate.instant('REFERENCES.SAVE_SUCCESS'));
        this.savingReference = false;
        this.showSaveReferenceModal = false;
      },
      error: (err: any) => {
        this.alert.showError(err, this.translate.instant('REFERENCES.SAVE_ERROR'));
        this.savingReference = false;
      }
    });
  }

  downloadJson(): void {
    if (!this.predictData) return;

    if (this.classifications.length > 0) {
      const jsonString = JSON.stringify(this.predictData, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const fileName = this.getArchiveName(this.selectedArchiveId).replace(/\.[^/.]+$/, '');
      a.download = `${fileName}_classification.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      return;
    }

    const kvData: { [key: string]: any } = {};
    
    const objectKeys = new Set<string>();
    if (this.objects && Array.isArray(this.objects)) {
      this.objects.forEach(obj => {
        if (obj.values) {
          Object.keys(obj.values).forEach(key => objectKeys.add(key));
        }
      });
    }

    if (this.entities && Array.isArray(this.entities)) {
      this.entities.forEach(entity => {
        if (entity.label && entity.text !== undefined && !objectKeys.has(entity.label)) {
          if (kvData[entity.label]) {
            if (Array.isArray(kvData[entity.label])) {
              kvData[entity.label].push(entity.text);
            } else {
              kvData[entity.label] = [kvData[entity.label], entity.text];
            }
          } else {
            kvData[entity.label] = entity.text;
          }
        }
      });
    }

    if (this.objects && Array.isArray(this.objects)) {
      this.objects.forEach(obj => {
        if (obj.relation && obj.values) {
          const objValues: { [key: string]: any } = {};
          Object.keys(obj.values).forEach(key => {
            const valArray = obj.values[key];
            if (Array.isArray(valArray)) {
              objValues[key] = valArray.map((v: any) => v.text);
              if (objValues[key].length === 1) objValues[key] = objValues[key][0];
            }
          });
          
          if (kvData[obj.relation]) {
            if (Array.isArray(kvData[obj.relation])) {
              kvData[obj.relation].push(objValues);
            } else {
              kvData[obj.relation] = [kvData[obj.relation], objValues];
            }
          } else {
            kvData[obj.relation] = objValues;
          }
        }
      });
    }

    const jsonString = JSON.stringify(kvData, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    
    const a = document.createElement('a');
    a.href = url;
    const fileName = this.getArchiveName(this.selectedArchiveId).replace(/\.[^/.]+$/, "");
    a.download = `${fileName}_result.json`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }

  previousArchive(): void {
    if (this.archives.length <= 1) return;
    const currentIndex = this.archives.findIndex(a => a.id === this.selectedArchiveId);
    if (currentIndex > 0) {
      this.selectedArchiveId = this.archives[currentIndex - 1].id;
      this.onArchiveSelect();
    }
  }

  nextArchive(): void {
    if (this.archives.length <= 1) return;
    const currentIndex = this.archives.findIndex(a => a.id === this.selectedArchiveId);
    if (currentIndex < this.archives.length - 1) {
      this.selectedArchiveId = this.archives[currentIndex + 1].id;
      this.onArchiveSelect();
    }
  }

  get isFirstArchive(): boolean {
    if (this.archives.length <= 1) return true;
    const currentIndex = this.archives.findIndex(a => a.id === this.selectedArchiveId);
    return currentIndex <= 0;
  }

  get isLastArchive(): boolean {
    if (this.archives.length <= 1) return true;
    const currentIndex = this.archives.findIndex(a => a.id === this.selectedArchiveId);
    return currentIndex >= this.archives.length - 1;
  }

  downloadAllResultsJson(): void {
    const zipName = this.resultName || this.interactionName;
    this.resultsService.downloadAllResultsAsZip(this.interactionId, this.resultId, zipName)
      .catch(error => {
        console.error('Error downloading all results:', error);
      });
  }

  get canSaveReference(): boolean {
    return !!this.selectedArchiveId && !this.savingReference;
  }

  ngOnDestroy(): void {
    this._stopReprocessPolling();
    this.resultsService.clearCache();
  }
}
